<template>
  <div>
    <imageable-control v-if="!create"
                       :imageable-id="form.id"
                       imageable-type="Product"></imageable-control>

    <div class="is-pulled-right">
      <button type="button" class="button is-primary" @click="submit()">Save Changes</button>
    </div>

    <form-group type="checkbox"
                label="<strong>Activate Product</strong>"
                id="listed"
                v-model="form.listed"
                :error="form.errors.get('listed')"
                @event="form.listen(form, $event)">
    </form-group>

    <form-group type="select"
                id="company"
                label="Show On Company Website</small>"
                v-model="form.company"
                :error="form.errors.get('company')"
                @event="form.listen(form, $event)"
                :option_name="'name'"
                :option_value="'value'"
                :options="[
                    {name: 'All Companies', value: ''},
                    {name: 'Crockett', value: 'CF'},
                    {name: 'Bells', value: 'BELLS'},
                ]">
    </form-group>

    <form-group type="text"
                label="Full Product Name"
                id="name"
                v-model="form.name"
                :error="form.errors.get('name')"
                @event="form.listen(form, $event)">
    </form-group>

    <div class="columns">
      <div class="column is-3">
        <form-group type="text"
                    label="SKU"
                    id="sku"
                    v-model="form.sku"
                    :error="form.errors.get('sku')"
                    @event="form.listen(form, $event)">
        </form-group>
      </div>
      <div class="column is-9">
        <form-group type="text"
                    label="Short Product Name"
                    id="short_name"
                    v-model="form.short_name"
                    :error="form.errors.get('short_name')"
                    @event="form.listen(form, $event)">
        </form-group>
      </div>
    </div>


    <form-group type="textarea"
                label="Description"
                id="description"
                v-model="form.description"
                :error="form.errors.get('description')"
                @event="form.listen(form, $event)">
    </form-group>

    <div class="columns">
      <div class="column is-6">
        <form-group type="text"
                    label="Price"
                    id="price"
                    v-model="form.price"
                    :error="form.errors.get('price')"
                    @event="form.listen(form, $event)">
        </form-group>
      </div>
      <div class="column is-6">
        <form-group type="text"
                    label="Shipping Cost <small>(blank for override)</small>"
                    id="shipping"
                    v-model="form.shipping"
                    :error="form.errors.get('shipping')"
                    @event="form.listen(form, $event)">
        </form-group>
        <label class="label text-danger" v-if="productParts.length > 0">
          Shipping cost will be overridden by subproducts
        </label>
      </div>
    </div>

    <div class="columns">
      <div class="column is-6">
        <form-group type="select"
                    label="Recurring Shipments <small>(months)</small>"
                    id="months"
                    v-model="form.months"
                    :error="form.errors.get('months')"
                    @event="form.listen(form, $event)"
                    :option_name="'name'"
                    :option_value="'value'"
                    :options="[
                    {name: 'None (Single Shipment)', value: ''},
                    {name: '2 Shipments', value: 2},
                    {name: '3 Shipments', value: 3},
                    {name: '4 Shipments', value: 4},
                    {name: '5 Shipments', value: 5},
                    {name: '6 Shipments', value: 6},
                    {name: '7 Shipments', value: 7},
                    {name: '8 Shipments', value: 8},
                    {name: '9 Shipments', value: 9},
                    {name: '10 Shipments', value: 10},
                    {name: '11 Shipments', value: 11},
                    {name: '12 Shipments', value: 12},
                ]">
        </form-group>
      </div>
      <div class="column is-6">
        <form-group type="text"
                    label="Weight <small>(approximate in Lbs.)</small>"
                    id="weight"
                    v-model="form.weight"
                    :error="form.errors.get('weight')"
                    @event="form.listen(form, $event)">
        </form-group>
      </div>
    </div>

    <p class="field">
      <label class="label">
        Orders Allowed Between
      </label>
      <small><em>(if shipping ends before the order end date, product cannot be ordered)</em></small>
      <span class="control">
          <slider-control :min="currentSeason.start.getTime()"
                          :max="currentSeason.end.getTime()"
                          :start="getOrderStartDate()"
                          :end="getOrderEndDate()"
                          :step="24 * 60 * 60 * 1000"
                          :update-display="formatDate"
                          @start="setOrderStart"
                          @end="setOrderEnd"
          ></slider-control>
      </span>
    </p>

    <p class="field">
      <label class="label">Available Shipping Months</label>
      <span class="control">
          <product-month-selector :starting-months="form.ship_months"
                                  @monthsChanged="form.ship_months = $event"></product-month-selector>
      </span>
      <label class="label text-danger" v-if="form.ship_months.length < form.months">
        You need to select more months or this product cannot be ordered!
      </label>
    </p>


    <div class="field">
      <a v-if="!editSlug" @click="startSlugEdit()">Edit Product URL</a>
      <a v-else @click="cancelSlugEdit()">Cancel editing URL</a>
    </div>

    <form-group v-if="editSlug"
                type="text"
                :label="'<small>crockettfarms.com/products/' + form.slug + '</small>'"
                id="slug"
                v-model="form.slug"
                :error="form.errors.get('slug')"
                @event="form.listen(form, $event)">
    </form-group>

    <div class="field">
      <a v-if="!editOrderDescription" @click="startOrderDescriptionEdit()">Add Special Order Information</a>
      <a v-else @click="cancelOrderDescriptionEdit()">Remove Special Order Information</a>
    </div>

    <form-group v-if="editOrderDescription"
                type="textarea"
                label="Order Information <small>(use for important information (look at SHC for example))</small>"
                id="ordering_description"
                v-model="form.ordering_description"
                :error="form.errors.get('ordering_description')"
                @event="form.listen(form, $event)">
    </form-group>

    <div class="is-clearfix">
      <div class="is-pulled-right">
        <button type="button" class="button is-primary" @click="submit()">Save Changes</button>
      </div>
    </div>
  </div>
</template>

<script>
import Form                                                                   from "../../helpers/Form"
import {CREATE_PRODUCT, UPDATE_PRODUCT}                                       from "../../store/mutation-types"
import {currentSeason, getYearOffset, formatDate, convertTimestampToMonthDay} from "../../utils/season"

export default {
  props: {
    product: {
      type: [String, Object], default: () => {
        return {
          listed: false,
          company: null,
          slug: null,
          sku: null,
          name: null,
          short_name: null,
          description: null,
          ordering_description: null,
          price: 0,
          shipping: 0,
          shipping_method: 'USPS',
          weight: null,
          months: 0,
          order_start: '09/1',
          order_end: '08/31',
          season_start: '09/1',
          season_end: '08/31',
          ship_months: [11, 12, 1, 2, 3],
        }
      }
    },
    create: { type: [Boolean], default: false }
  },
  data() {
    return {
      form: new Form(this, this.product, this.create ? CREATE_PRODUCT : UPDATE_PRODUCT),
      currentDate: new Date(),
      currentSeason,
      editSlug: false,
      editOrderDescription: (this.product.ordering_description),
    }
  },
  computed: {
    productParts() {
      if (typeof this.product.id === 'undefined') {
        return []
      }

      let parts = this.$store.state.products.product_parts.filter(part => part.product_id === this.product.id)
      let ids   = [];
      for (let part in parts) {
        ids.push(parts[part].product_part_id)
      }

      return this.$store.state.products.all.filter(product => ids.indexOf(product.id) !== -1)
    }
  },
  methods: {
    submit() {
      this.form.submit().then(data => {
        this.$emit('saved', data)
      })
    },
    getOrderStartDate() {
      let offset = getYearOffset(this.product.order_start, this.product.order_end)
      return new Date(this.product.order_start + '/' + (this.currentDate.getFullYear() + offset.start)).getTime()
    },
    getOrderEndDate() {
      let offset = getYearOffset(this.product.order_start, this.product.order_end)
      return new Date(this.product.order_end + '/' + (this.currentDate.getFullYear() + offset.end)).getTime()
    },
    getShippingStartDate() {
      let offset = getYearOffset(this.product.season_start, this.product.season_end)
      return new Date(this.product.season_start + '/' + (this.currentDate.getFullYear() + offset.start)).getTime()
    },
    getShippingEndDate() {
      let offset = getYearOffset(this.product.season_start, this.product.season_end)
      return new Date(this.product.season_end + '/' + (this.currentDate.getFullYear() + offset.end)).getTime()
    },
    formatDate(date) {
      return formatDate(new Date(+date))
    },
    convertTimestamp(timestamp) {
      return convertTimestampToMonthDay(timestamp)
    },
    setOrderStart(timestamp) {
      this.form.order_start = this.convertTimestamp(timestamp)
    },
    setOrderEnd(timestamp) {
      this.form.order_end = this.convertTimestamp(timestamp)
    },
    startSlugEdit() {
      this.editSlug = true;
    },
    cancelSlugEdit() {
      this.editSlug  = false;
      this.form.slug = this.product.slug;
    },
    startOrderDescriptionEdit() {
      this.editOrderDescription = true;
    },
    cancelOrderDescriptionEdit() {
      this.editOrderDescription      = false;
      this.form.ordering_description = null;
    }
  }
};
</script>



// WEBPACK FOOTER //
// ProductForm.vue?44fd455f